import {EnvHelper} from '@/helpers/EnvHelper'
import {RpcHelper} from '@/helpers/RpcHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'
import axios from 'axios'
import {u} from '@cityofzion/neon-js'
import {wallet} from '@cityofzion/neon-core'
import {$} from '@/facade'
import {NetworkNamespace} from '@/enums/NetworkNamespace'

type GetActiveProjectsAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Array'
        value: any[]
      }
    ]
  }
}

type GetActiveRoundAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Integer'
        value: string
      }
    ]
  }
}

type GetClaimableAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Integer'
        value: string
      }
    ]
  }
}

type InvokeFunctionAxiosResponse = {
  result: {
    stack: any[]
  }
}

type GetCurrentRoundAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Integer'
        value: string
      }
    ]
  }
}

type GetLastRoundAxiosResponse = GetCurrentRoundAxiosResponse

type GetOwnersAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Array'
        value: [
          {
            type: 'ByteString'
            value: string
          }
        ]
      }
    ]
  }
}

type OrbitProjectResponse = {
  type: 'Map'
  value: [
    {
      key: {
        type: 'ByteString'
        value: string // Project
      }
      value: {
        type: 'ByteString'
        value: string
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Token
      }
      value: {
        type: 'ByteString'
        value: string
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Amount
      }
      value: {
        type: 'Integer'
        value: string
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Type
      }
      value: {
        type: 'Integer'
        value: string
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Funded
      }
      value: {
        type: 'Boolean'
        value: boolean
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Active
      }
      value: {
        type: 'Boolean'
        value: boolean
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Round
      }
      value: {
        type: 'Integer'
        value: string
      }
    },
    {
      key: {
        type: 'ByteString'
        value: string // Instance ID
      }
      value: {
        type: 'Integer'
        value: string
      }
    }
  ]
}

type GetAllProjectAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Array'
        value: OrbitProjectResponse[]
      }
    ]
  }
}

type GetAllRoundsAxiosResponse = {
  result: {
    stack: Array<{type: string; value?: unknown}>
  }
}

type GetProjectAxiosResponse = {
  result: {
    stack: OrbitProjectResponse[]
  }
}

type GetProjectInstanceAxiosResponse = {
  result: {
    stack: OrbitProjectResponse[]
  }
}

type GetProjectInstanceIdAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Integer'
        value: string
      }
    ]
  }
}

type GetTotalRoundsAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Integer'
        value: string
      }
    ]
  }
}

type GetUserClaimablesAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Array'
        value: any[]
      }
    ]
  }
}

type IsPausedAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Boolean'
        value: boolean
      }
    ]
  }
}

type IsRoundActiveAxiosResponse = {
  result: {
    stack: [
      {
        type: 'Boolean'
        value: boolean
      }
    ]
  }
}

export type OrbitConfigMs = {
  roundDuration: number
  claimDuration: number
  distributionDuration: number
  lockoutDuration: number
}

export type ParsedOrbitRoundInfo = {
  roundNumber: number
  startTime: number
  endTime: number
  claimEndTime: number
  distributionEndTime: number
  finishedTime: number
  finished: boolean
  roundDuration: number
  claimDuration: number
  distributionDuration: number
  lockoutDuration: number
}

/** One project row nested under `getAllRounds` → round → Projects. */
export type SyncOrbitProjectFromContract = {
  project: string
  token: string
  amount: string | number
  assetType: number | string
  funded: boolean
  active: boolean
  round: number
  instanceId: number
  eventName: string | null
}

/** One round row from `getAllRounds` (Map on the VM stack). */
export type SyncOrbitRoundFromContract = {
  roundNumber: number
  startTime: number
  endTime: number
  claimEndTime: number
  distributionEndTime: number
  finishedTime: number
  finished: boolean
  roundDuration: number
  claimDuration: number
  distributionDuration: number
  lockoutDuration: number
  projects: SyncOrbitProjectFromContract[]
}

/** Contract defaults when `setupOrbit` was never stored (matches on-chain behavior). */
export const ORBIT_DEFAULT_CONFIG_MS: OrbitConfigMs = {
  roundDuration: 14 * 24 * 60 * 60 * 1000,
  claimDuration: 7 * 24 * 60 * 60 * 1000,
  distributionDuration: 7 * 24 * 60 * 60 * 1000,
  lockoutDuration: 30 * 24 * 60 * 60 * 1000,
}

const MS_MINUTE = 60 * 1000
const MS_HOUR = 60 * MS_MINUTE
const MS_DAY = 24 * MS_HOUR

function trimDecimalString(s: string): string {
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
}

function formatDecimalQuantity(n: number, maxDecimals: number): string {
  if (!Number.isFinite(n)) return '0'
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n))
  const factor = 10 ** maxDecimals
  const r = Math.round(n * factor) / factor
  return trimDecimalString(r.toFixed(maxDecimals))
}

/**
 * Human-readable orbit phase length from contract ms: ≥1 day as days, else ≥1 h as hours, else minutes.
 */
export function formatOrbitPhaseDurationMs(
  ms: number,
  // eslint-disable-next-line no-unused-vars -- callback type parameter names for documentation only
  t: (key: string, values?: Record<string, string | number>) => string
): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) {
    return t('view.ListOrbitView.phaseLengthInvalid')
  }
  if (ms >= MS_DAY) {
    const days = ms / MS_DAY
    if (Math.abs(days - 1) < 1e-9) {
      return t('view.ListOrbitView.phaseLengthOneDay')
    }
    return t('view.ListOrbitView.phaseLengthDays', {
      value: formatDecimalQuantity(days, 2),
    })
  }
  if (ms >= MS_HOUR) {
    const hours = ms / MS_HOUR
    if (Math.abs(hours - 1) < 1e-9) {
      return t('view.ListOrbitView.phaseLengthOneHour')
    }
    return t('view.ListOrbitView.phaseLengthHours', {
      value: formatDecimalQuantity(hours, 2),
    })
  }
  const minutes = Math.max(1, Math.round(ms / MS_MINUTE))
  if (minutes === 1) {
    return t('view.ListOrbitView.phaseLengthOneMinute')
  }
  return t('view.ListOrbitView.phaseLengthMinutes', {value: String(minutes)})
}

export class OrbitHelper {
  /**
   * Resolves the RPC endpoint + Orbit contract hash for the network in use
   * ('neo3/mainnet' | 'neo3/testnet'), the one picked in the network bar. The RPC
   * is the healthiest among that network's redundant endpoints
   * (see {@link RpcHelper.getBestRpc}).
   *
   * Pass `network` only to read a network the bar is NOT on — the owners list
   * shows both. Anything that SIGNS must stay on the bar's network, since that is
   * the one the wallet adapters and invocation builders are wired to.
   */
  private static async resolveOrbitContext(
    network?: string | null
  ): Promise<{
    rpcUrl: string
    scriptHash: string
  }> {
    const resolved = network ?? currentN3Network()
    const rpcUrl = await RpcHelper.getBestRpc(EnvHelper.rpcUrls(resolved))
    const scriptHash = EnvHelper.orbitScriptHash(resolved)
    return {rpcUrl, scriptHash}
  }

  /** Neo timestamps from the VM are usually ms; tolerate second-scale values. */
  static normalizeContractTimeMs(v: number): number {
    if (v == null || v === 0) return v
    return v < 1e12 ? v * 1000 : v
  }

  private static parseStackUtf8Map(
    stackItem: any
  ): Record<string, number | boolean> {
    const out: Record<string, number | boolean> = {}
    if (!stackItem || stackItem.type === 'Any' || stackItem.type === 'Null') {
      return out
    }
    if (stackItem.type !== 'Map') return out
    const pairs = stackItem.value
    if (!Array.isArray(pairs)) return out

    for (const pair of pairs) {
      const keyNode = pair?.key ?? pair?.Key
      const valNode = pair?.value ?? pair?.Value
      const keyB64 = keyNode?.value ?? keyNode
      if (!keyB64 || typeof keyB64 !== 'string') continue

      let keyStr: string
      try {
        keyStr = Buffer.from(keyB64, 'base64')
          .toString('utf8')
          .trim()
      } catch {
        continue
      }

      if (valNode?.type === 'Integer') {
        out[keyStr] = Number(valNode.value ?? 0)
      } else if (valNode?.type === 'Boolean') {
        out[keyStr] = valNode.value === true || valNode.Value === true
      }
    }
    return out
  }

  private static pickNumber(
    m: Record<string, number | boolean>,
    keys: string[]
  ): number | undefined {
    for (const k of keys) {
      const v = m[k]
      if (typeof v === 'number' && !Number.isNaN(v)) return v
    }
    return undefined
  }

  private static pickBool(
    m: Record<string, number | boolean>,
    keys: string[]
  ): boolean {
    for (const k of keys) {
      const v = m[k]
      if (typeof v === 'boolean') return v
    }
    return false
  }

  private static decodeStackMapKeyUtf8(pair: any): string | null {
    const keyNode = pair?.key ?? pair?.Key
    const keyB64 = keyNode?.value ?? keyNode
    if (!keyB64 || typeof keyB64 !== 'string') return null
    try {
      return Buffer.from(keyB64, 'base64')
        .toString('utf8')
        .trim()
    } catch {
      return null
    }
  }

  private static decodeStackByteStringHash160(valNode: any): string | null {
    if (
      !valNode ||
      (valNode.type !== 'ByteString' && valNode.type !== 'Buffer')
    ) {
      return null
    }
    const raw = valNode.value ?? valNode.Value
    if (!raw || typeof raw !== 'string') return null
    try {
      const hex = Buffer.from(raw, 'base64').toString('hex')
      return `0x${u.reverseHex(hex)}`
    } catch {
      return null
    }
  }

  private static decodeStackIntegerLoose(valNode: any): number {
    if (!valNode) return 0
    if (valNode.type === 'Integer') {
      return Number(valNode.value ?? valNode.Value ?? 0)
    }
    const v = valNode.value ?? valNode.Value
    if (v != null && v !== '') return Number(v)
    return 0
  }

  private static decodeContractEventName(valNode: any): string | null {
    if (!valNode) return null

    const raw = valNode.value ?? valNode.Value
    if (raw == null || typeof raw !== 'string' || raw === '') return null

    let decoded: string
    try {
      decoded = Buffer.from(raw, 'base64')
        .toString('utf8')
        .trim()
    } catch {
      return null
    }

    if (!decoded || decoded.toLowerCase() === 'all') return null

    return decoded
  }

  /**
   * Same layout as `getAllProjects` items: Array<9 stack items>.
   */
  private static decodeInlineStructProject(
    parts: any[]
  ): SyncOrbitProjectFromContract | null {
    if (!Array.isArray(parts) || parts.length < 8) return null
    try {
      return {
        project: `0x${u.reverseHex(
          Buffer.from(parts[0].value.value, 'base64').toString('hex')
        )}`,
        token: `0x${u.reverseHex(
          Buffer.from(parts[1].value.value, 'base64').toString('hex')
        )}`,
        amount: parts[2].value.value,
        assetType: parts[3].value.value,
        funded: parts[4].value.value,
        active: parts[5].value.value,
        round: Number(parts[6].value.value),
        instanceId: Number(parts[7].value.value),
        eventName: this.decodeContractEventName(parts[8]?.value),
      }
    } catch {
      return null
    }
  }

  private static parseContractProjectMap(
    mapItem: any
  ): SyncOrbitProjectFromContract | null {
    if (!mapItem || mapItem.type !== 'Map') return null
    const pairs = mapItem.value
    if (!Array.isArray(pairs)) return null
    const row: Record<string, any> = {}

    for (const pair of pairs) {
      const keyStr = this.decodeStackMapKeyUtf8(pair)
      const val = pair?.value ?? pair?.Value
      if (!keyStr || val == null) continue

      switch (keyStr) {
        case 'Project':
          row.project = this.decodeStackByteStringHash160(val)
          break
        case 'Token':
          row.token = this.decodeStackByteStringHash160(val)
          break
        case 'Amount':
          row.amount =
            val.type === 'Integer'
              ? val.value ?? val.Value ?? 0
              : val.value ?? val.Value
          break
        case 'AssetType':
        case 'Type':
          row.assetType = this.decodeStackIntegerLoose(val)
          break
        case 'Funded':
          row.funded =
            val.type === 'Boolean'
              ? val.value === true || val.Value === true
              : !!this.decodeStackIntegerLoose(val)
          break
        case 'Active':
          row.active =
            val.type === 'Boolean'
              ? val.value === true || val.Value === true
              : !!this.decodeStackIntegerLoose(val)
          break
        case 'Round':
          row.round = this.decodeStackIntegerLoose(val)
          break
        case 'InstanceId':
          row.instanceId = this.decodeStackIntegerLoose(val)
          break
        case 'EventName':
          row.eventName = this.decodeContractEventName(val)
          break
        default:
          break
      }
    }

    if (!row.project || !row.token) return null
    return {
      project: row.project,
      token: row.token,
      amount: row.amount ?? 0,
      assetType: row.assetType ?? 0,
      funded: !!row.funded,
      active: row.active !== false,
      round: Number(row.round ?? 0),
      instanceId: Number(row.instanceId ?? 0),
      eventName: row.eventName ?? null,
    }
  }

  private static parseContractProjectFromStackItem(
    item: any
  ): SyncOrbitProjectFromContract | null {
    if (!item) return null
    if (item.type === 'Map') return this.parseContractProjectMap(item)
    if (item.type === 'Array' && Array.isArray(item.value)) {
      return this.decodeInlineStructProject(item.value)
    }
    return null
  }

  private static parseGetAllRoundsRoundMap(
    mapItem: any
  ): SyncOrbitRoundFromContract | null {
    if (!mapItem || mapItem.type !== 'Map') return null
    const pairs = mapItem.value
    if (!Array.isArray(pairs)) return null

    let roundNumber = 0
    let startTime = 0
    let endTime = 0
    let claimEndTime = 0
    let distributionEndTime = 0
    let finishedTime = 0
    let finished = false
    let roundDuration = 0
    let claimDuration = 0
    let distributionDuration = 0
    let lockoutDuration = 0
    const projects: SyncOrbitProjectFromContract[] = []

    for (const pair of pairs) {
      const keyStr = this.decodeStackMapKeyUtf8(pair)
      const val = pair?.value ?? pair?.Value
      if (!keyStr || val == null) continue

      switch (keyStr) {
        case 'RoundNumber':
          roundNumber = this.decodeStackIntegerLoose(val)
          break
        case 'StartTime':
          startTime = this.decodeStackIntegerLoose(val)
          break
        case 'EndTime':
          endTime = this.decodeStackIntegerLoose(val)
          break
        case 'ClaimEndTime':
          claimEndTime = this.decodeStackIntegerLoose(val)
          break
        case 'DistributionEndTime':
          distributionEndTime = this.decodeStackIntegerLoose(val)
          break
        case 'FinishedTime':
          finishedTime = this.decodeStackIntegerLoose(val)
          break
        case 'Finished':
          finished =
            val.type === 'Boolean'
              ? val.value === true || val.Value === true
              : !!this.decodeStackIntegerLoose(val)
          break
        case 'RoundDuration':
          roundDuration = this.decodeStackIntegerLoose(val)
          break
        case 'ClaimDuration':
          claimDuration = this.decodeStackIntegerLoose(val)
          break
        case 'DistributionDuration':
          distributionDuration = this.decodeStackIntegerLoose(val)
          break
        case 'LockoutDuration':
          lockoutDuration = this.decodeStackIntegerLoose(val)
          break
        case 'Projects':
          if (val.type === 'Array' && Array.isArray(val.value)) {
            for (const el of val.value) {
              const p = this.parseContractProjectFromStackItem(el)
              if (p) projects.push(p)
            }
          }
          break
        default:
          break
      }
    }

    if (roundNumber == null || Number.isNaN(roundNumber) || roundNumber <= 0) {
      return null
    }

    return {
      roundNumber,
      startTime,
      endTime,
      claimEndTime,
      distributionEndTime,
      finishedTime,
      finished,
      roundDuration,
      claimDuration,
      distributionDuration,
      lockoutDuration,
      projects,
    }
  }

  /**
   * Full orbit state: each round includes nested projects (single `invokefunction` call).
   */
  static async getAllRounds(
    network?: string | null
  ): Promise<SyncOrbitRoundFromContract[]> {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<GetAllRoundsAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getAllRounds', []],
      id: 1,
    })) as {data: GetAllRoundsAxiosResponse}

    const stack0 = data?.result?.stack?.[0] as any
    if (!stack0 || stack0.type !== 'Array' || !Array.isArray(stack0.value)) {
      return []
    }

    return stack0.value
      .map((item: any) => this.parseGetAllRoundsRoundMap(item))
      .filter(
        (
          r: SyncOrbitRoundFromContract | null
        ): r is SyncOrbitRoundFromContract => r !== null
      )
  }

  /**
   * Best-effort decode for `getUserClaimables` stack output.
   *
   * Expected (common) pattern: Array<Struct<[Hash160 projectScriptHash, Integer instanceId, ...]>>.
   * This method intentionally tolerates variations and just extracts what we need to call `claim`.
   */
  static decodeUserClaimables(
    claimables: any[]
  ): Array<{projectScriptHash: string; instanceId: string}> {
    if (!Array.isArray(claimables)) return []

    const decodeHash160 = (node: any): string | null => {
      const raw = node?.value
      if (!raw || typeof raw !== 'string') return null

      // Sometimes RPC returns base64 bytes
      if (raw.includes('=') || raw.includes('/') || raw.includes('+')) {
        try {
          const hexBE = Buffer.from(raw, 'base64').toString('hex')
          return u.reverseHex(hexBE)
        } catch (_) {
          // fallthrough
        }
      }

      // Sometimes RPC returns hex already (with or without 0x)
      const hex = raw.startsWith('0x') ? raw.slice(2) : raw
      if (/^[0-9a-fA-F]{40}$/.test(hex)) {
        return hex.toLowerCase()
      }

      return null
    }

    const decodeInt = (node: any): string | null => {
      const v = node?.value
      if (v === undefined || v === null) return null
      return String(v)
    }

    const out: Array<{projectScriptHash: string; instanceId: string}> = []

    for (const entry of claimables) {
      const structValues: any[] = entry?.value
      if (!Array.isArray(structValues) || structValues.length < 2) continue

      const projectScriptHash = decodeHash160(structValues[0])
      const instanceId = decodeInt(structValues[1])

      if (!projectScriptHash || !instanceId) continue
      out.push({projectScriptHash, instanceId})
    }

    return out
  }

  /**
   * Decodes getUserClaimables response when it returns Array of Map.
   * Map keys: User, Project, Amount, TokenId, Claimable, Round, InstanceId
   */
  static decodeUserClaimablesMap(
    claimables: any
  ): Array<{
    project: string
    instanceId: number
    round: number
    claimable: boolean
  }> {
    const arr = Array.isArray(claimables) ? claimables : claimables?.value ?? []
    if (!Array.isArray(arr) || arr.length === 0) return []

    const decodeHash160 = (node: any): string | null => {
      if (!node) return null
      const raw = node?.value ?? node
      if (raw == null) return null
      const str = typeof raw === 'string' ? raw : String(raw)
      if (!str) return null
      try {
        if (str.includes('=') || str.includes('/') || str.includes('+')) {
          const hexBE = Buffer.from(str, 'base64').toString('hex')
          return (u.reverseHex(hexBE) || '').replace(/^0x/i, '').toLowerCase()
        }
        return (str.startsWith('0x') ? str.slice(2) : str).toLowerCase()
      } catch {
        return null
      }
    }

    const keyToField: Record<string, string> = {
      User: 'user',
      Project: 'project',
      Amount: 'amount',
      TokenId: 'tokenId',
      Claimable: 'claimable',
      Round: 'round',
      InstanceId: 'instanceId',
    }

    const out: Array<{
      project: string
      instanceId: number
      round: number
      claimable: boolean
    }> = []

    for (const entry of arr) {
      const mapPairs = entry?.value ?? entry
      if (!Array.isArray(mapPairs)) continue

      const row: Record<string, any> = {}

      for (const pair of mapPairs) {
        const keyNode = pair?.key ?? pair?.Key
        const valNode = pair?.value ?? pair?.Value
        const keyB64 =
          keyNode && typeof keyNode === 'object' && 'value' in keyNode
            ? keyNode.value
            : keyNode
        if (!keyB64 || typeof keyB64 !== 'string') continue

        let keyStr: string
        try {
          keyStr = Buffer.from(keyB64, 'base64')
            .toString('utf8')
            .trim()
        } catch {
          continue
        }
        const fieldName = keyToField[keyStr] ?? keyStr
        const val = valNode

        if (!val) continue
        if (val?.type === 'ByteString') {
          row[fieldName] = decodeHash160(val)
        } else if (val?.type === 'Integer') {
          row[fieldName] = Number(val.value ?? val.Value ?? 0)
        } else if (val?.type === 'Boolean') {
          row[fieldName] = val.value === true || val.Value === true
        } else {
          row[fieldName] = val?.value ?? val?.Value
        }
      }

      const project = row.project
      const instanceId = row.instanceId
      const round = row.round
      const claimable = row.claimable === false ? false : true

      if (
        project != null &&
        project !== '' &&
        instanceId != null &&
        !Number.isNaN(instanceId) &&
        round != null &&
        !Number.isNaN(round)
      ) {
        out.push({project, instanceId, round, claimable})
      }
    }

    return out
  }

  static async getActiveProjects() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetActiveProjectsAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getActiveProjects'],
      id: 1,
    })) as {data: GetActiveProjectsAxiosResponse}

    return data.result.stack[0].value
  }

  static async getActiveRounds() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetActiveRoundAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getActiveRound'],
      id: 1,
    })) as {data: GetClaimableAxiosResponse}

    return data.result.stack[0].value
  }

  static async getClaimable(userScriptHash: string, projectScriptHash: string) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetClaimableAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [
        scriptHash,
        'getActiveProjects',
        [
          {
            type: 'Hash160',
            value: userScriptHash,
          },
          {
            type: 'Hash160',
            value: projectScriptHash,
          },
        ],
      ],
      id: 1,
    })) as {data: GetClaimableAxiosResponse}

    return data.result.stack[0].value
  }

  static async getContractStats(network?: string | null) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<InvokeFunctionAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getContractStats'],
      id: 1,
    })) as {data: InvokeFunctionAxiosResponse}

    return data.result.stack[0]
  }

  /**
   * Active-phase end time for the current round (ms), from `getContractStats` or `getRoundInfo`.
   * Replaces removed `getRoundEndTime` on the contract.
   */
  static async getRoundEndTimeMs(network?: string | null): Promise<number> {
    const stack0 = await this.getContractStats(network)

    if (stack0?.type === 'Integer') {
      return this.normalizeContractTimeMs(Number(stack0.value))
    }

    const m = this.parseStackUtf8Map(stack0)
    const t = this.pickNumber(m, ['RoundEndTime', 'roundEndTime'])
    if (t != null && t > 0) {
      return this.normalizeContractTimeMs(t)
    }

    const ar = Number(await this.getActiveRound(network))
    if (!ar || ar <= 0) return 0

    const info = await this.getRoundInfoParsed(ar, network)
    const et = info?.endTime
    if (et != null && et > 0) {
      return this.normalizeContractTimeMs(et)
    }

    return 0
  }

  /** Raw `getOrbitConfig` map, or null if never stored (use {@link resolveOrbitConfig} for defaults). */
  static async getOrbitConfigRaw(): Promise<Partial<OrbitConfigMs> | null> {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<InvokeFunctionAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getOrbitConfig'],
      id: 1,
    })) as {data: InvokeFunctionAxiosResponse}

    const stack0 = data?.result?.stack?.[0]
    if (!stack0 || stack0.type === 'Any' || stack0.type === 'Null') {
      return null
    }

    const m = this.parseStackUtf8Map(stack0)
    const roundDuration = this.pickNumber(m, ['RoundDuration', 'roundDuration'])
    const claimDuration = this.pickNumber(m, ['ClaimDuration', 'claimDuration'])
    const distributionDuration = this.pickNumber(m, [
      'DistributionDuration',
      'distributionDuration',
    ])
    const lockoutDuration = this.pickNumber(m, [
      'LockoutDuration',
      'lockoutDuration',
    ])

    if (
      roundDuration == null &&
      claimDuration == null &&
      distributionDuration == null &&
      lockoutDuration == null
    ) {
      return null
    }

    return {
      roundDuration,
      claimDuration,
      distributionDuration,
      lockoutDuration,
    }
  }

  static async resolveOrbitConfig(): Promise<OrbitConfigMs> {
    const raw = await this.getOrbitConfigRaw()
    return {
      roundDuration:
        raw?.roundDuration ?? ORBIT_DEFAULT_CONFIG_MS.roundDuration,
      claimDuration:
        raw?.claimDuration ?? ORBIT_DEFAULT_CONFIG_MS.claimDuration,
      distributionDuration:
        raw?.distributionDuration ??
        ORBIT_DEFAULT_CONFIG_MS.distributionDuration,
      lockoutDuration:
        raw?.lockoutDuration ?? ORBIT_DEFAULT_CONFIG_MS.lockoutDuration,
    }
  }

  /** Phase boundaries (ms) after active phase ends, using a config snapshot. */
  static phaseEndTimesFromActiveEndMs(
    activePhaseEndMs: number,
    cfg: OrbitConfigMs
  ): {
    claimEndMs: number
    distributionEndMs: number
    finishedMs: number
  } {
    const claimEndMs = activePhaseEndMs + cfg.claimDuration
    const distributionEndMs = claimEndMs + cfg.distributionDuration
    const finishedMs = distributionEndMs + cfg.lockoutDuration
    return {claimEndMs, distributionEndMs, finishedMs}
  }

  static async getRoundInfoParsed(
    roundNumber: number,
    network?: string | null
  ): Promise<ParsedOrbitRoundInfo | null> {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<InvokeFunctionAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [
        scriptHash,
        'getRoundInfo',
        [
          {
            type: 'Integer',
            value: String(roundNumber),
          },
        ],
      ],
      id: 1,
    })) as {data: InvokeFunctionAxiosResponse}

    const stack0 = data?.result?.stack?.[0]
    if (!stack0 || stack0.type === 'Any' || stack0.type === 'Null') {
      return null
    }

    const m = this.parseStackUtf8Map(stack0)
    return {
      roundNumber:
        this.pickNumber(m, ['RoundNumber', 'roundNumber']) ?? roundNumber,
      startTime: this.pickNumber(m, ['StartTime', 'startTime']) ?? 0,
      endTime: this.pickNumber(m, ['EndTime', 'endTime']) ?? 0,
      claimEndTime: this.pickNumber(m, ['ClaimEndTime', 'claimEndTime']) ?? 0,
      distributionEndTime:
        this.pickNumber(m, ['DistributionEndTime', 'distributionEndTime']) ?? 0,
      finishedTime: this.pickNumber(m, ['FinishedTime', 'finishedTime']) ?? 0,
      finished: this.pickBool(m, ['Finished', 'finished']),
      roundDuration:
        this.pickNumber(m, ['RoundDuration', 'roundDuration']) ?? 0,
      claimDuration:
        this.pickNumber(m, ['ClaimDuration', 'claimDuration']) ?? 0,
      distributionDuration:
        this.pickNumber(m, ['DistributionDuration', 'distributionDuration']) ??
        0,
      lockoutDuration:
        this.pickNumber(m, ['LockoutDuration', 'lockoutDuration']) ?? 0,
    }
  }

  /**
   * Claim deadline (ms) for UI: prefers on-chain `ClaimEndTime`, else DB active-phase end + resolved claim duration.
   */
  static async resolveClaimEndTimeMs(
    roundNumber: number,
    activePhaseEndFromDbMs: number | null
  ): Promise<number | null> {
    try {
      const info = await this.getRoundInfoParsed(roundNumber)
      const ce = info?.claimEndTime
      if (ce != null && ce > 0) {
        return this.normalizeContractTimeMs(ce)
      }
    } catch {
      /* RPC / contract mismatch */
    }

    if (
      activePhaseEndFromDbMs != null &&
      !Number.isNaN(activePhaseEndFromDbMs) &&
      activePhaseEndFromDbMs > 0
    ) {
      const cfg = await this.resolveOrbitConfig()
      return activePhaseEndFromDbMs + cfg.claimDuration
    }

    return null
  }

  static claimEndMsForReward(
    reward: {
      orbitRound?: {round?: number | null; endsAt?: unknown} | null
    },
    claimEndByRound: Record<number, number>
  ): number | null {
    const r = reward.orbitRound?.round
    if (r == null) return null
    const ms = claimEndByRound[Number(r)]
    return ms != null && ms > 0 ? ms : null
  }

  static async buildClaimEndByRoundMap(
    items: Array<{
      orbitRound?: {
        round?: number | null
        endsAt?: unknown
        claimEndsAt?: unknown
      } | null
    }>
  ): Promise<Record<number, number>> {
    const out: Record<number, number> = {}
    const rounds = new Set<number>()
    for (const rw of items) {
      const rn = rw.orbitRound?.round
      if (rn != null) rounds.add(Number(rn))
    }

    await Promise.all(
      [...rounds].map(async rn => {
        const sample = items.find(r => Number(r.orbitRound?.round) === rn)
        const claimStored = sample?.orbitRound?.claimEndsAt
        if (claimStored) {
          const tClaim = new Date(
            claimStored as string | number | Date
          ).getTime()
          if (!Number.isNaN(tClaim) && tClaim > 0) {
            out[rn] = tClaim
            return
          }
        }
        let endsMs: number | null = null
        const ea = sample?.orbitRound?.endsAt
        if (ea) {
          const t = new Date(ea as string | number | Date).getTime()
          endsMs = Number.isNaN(t) ? null : t
        }
        const ms = await this.resolveClaimEndTimeMs(rn, endsMs)
        if (ms != null && ms > 0) {
          out[rn] = ms
        }
      })
    )

    return out
  }

  static async getActiveRound(network?: string | null) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<GetActiveRoundAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getActiveRound'],
      id: 1,
    })) as {data: GetActiveRoundAxiosResponse}

    return data.result.stack[0].value
  }

  static async getLastRound() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetLastRoundAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getLastRound'],
      id: 1,
    })) as {data: GetCurrentRoundAxiosResponse}

    return data.result.stack[0].value
  }

  /** Owners of the Orbit contract on `network`, or on the bar's network by default. */
  static async getOwners(network?: string | null) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<GetOwnersAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getOwners'],
      id: 1,
    })) as {data: GetOwnersAxiosResponse}

    const owners: string[] = []

    data.result.stack[0].value.map(owner => {
      const hexBE = Buffer.from(owner.value, 'base64').toString('hex')

      const hexLE = u.reverseHex(hexBE)

      const address = wallet.getAddressFromScriptHash(hexLE)

      owners.push(address)
    })

    return owners
  }

  static async getFundedProjectKeys(transactionHash: string) {
    const {data} = await axios.post(await RpcHelper.getActiveRpc(), {
      jsonrpc: '2.0',
      method: 'getapplicationlog',
      params: [transactionHash],
      id: 1,
    })

    const notifications: any[] =
      data?.result?.executions?.[0]?.notifications ?? []
    const orbitScriptHash = EnvHelper.orbitScriptHash(
      currentN3Network()
    ).toLowerCase()

    return new Set(
      notifications
        .filter(
          n =>
            n.eventname === 'ProjectFunded' &&
            n.contract?.toLowerCase() === orbitScriptHash
        )
        .map(n => {
          const [project, , instanceId] = n.state.value
          const projectHash = u.reverseHex(
            Buffer.from(project.value, 'base64').toString('hex')
          )
          return this.projectKey(`0x${projectHash}`, instanceId.value)
        })
    )
  }

  static projectKey(projectHash: string, instanceId: number | string) {
    return `${projectHash.toLowerCase()}_${Number(instanceId)}`
  }

  static async getAllProjects(
    network?: string | null
  ): Promise<SyncOrbitProjectFromContract[]> {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext(network)

    const {data} = (await axios.post<GetAllProjectAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getAllProjects', []],
      id: 1,
    })) as {data: GetAllProjectAxiosResponse}

    const stackItems = (data as any)?.result?.stack?.[0]?.value

    if (!Array.isArray(stackItems)) return []

    return stackItems
      .map(item => this.parseContractProjectFromStackItem(item))
      .filter((row): row is SyncOrbitProjectFromContract => row !== null)
  }

  static async getProject(projectScriptHash: string) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetProjectAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [
        scriptHash,
        'getProject',
        [
          {
            type: 'Hash160',
            value: projectScriptHash,
          },
        ],
      ],
      id: 1,
    })) as {data: GetProjectAxiosResponse}

    return {
      project: `0x${u.reverseHex(
        Buffer.from(
          data.result.stack[0].value[0].value.value,
          'base64'
        ).toString('hex')
      )}`,
      token: `0x${u.reverseHex(
        Buffer.from(
          data.result.stack[0].value[1].value.value,
          'base64'
        ).toString('hex')
      )}`,
      amount: data.result.stack[0].value[2].value.value,
      assetType: data.result.stack[0].value[3].value.value,
      funded: data.result.stack[0].value[4].value.value,
      active: data.result.stack[0].value[5].value.value,
      round: data.result.stack[0].value[6].value.value,
      instanceId: data.result.stack[0].value[7].value.value,
    }
  }

  static async getProjectInstance(
    projectScriptHash: string,
    instanceId: string
  ) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetProjectInstanceAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [
        scriptHash,
        'getProjectInstance',
        [
          {
            type: 'Hash160',
            value: projectScriptHash,
          },
          {
            type: 'Integer',
            value: instanceId,
          },
        ],
      ],
      id: 1,
    })) as {data: GetProjectInstanceAxiosResponse}

    return {
      project: `0x${u.reverseHex(
        Buffer.from(
          data.result.stack[0].value[0].value.value,
          'base64'
        ).toString('hex')
      )}`,
      token: `0x${u.reverseHex(
        Buffer.from(
          data.result.stack[0].value[1].value.value,
          'base64'
        ).toString('hex')
      )}`,
      amount: data.result.stack[0].value[2].value.value,
      assetType: data.result.stack[0].value[3].value.value,
      funded: data.result.stack[0].value[4].value.value,
      active: data.result.stack[0].value[5].value.value,
      round: data.result.stack[0].value[6].value.value,
      instanceId: data.result.stack[0].value[7].value.value,
    }
  }

  static async getProjectInstanceId(projectScriptHash: string) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetProjectInstanceIdAxiosResponse>(
      rpcUrl,
      {
        jsonrpc: '2.0',
        method: 'invokefunction',
        params: [
          scriptHash,
          'getProjectInstanceId',
          [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
          ],
        ],
        id: 1,
      }
    )) as {data: GetProjectInstanceIdAxiosResponse}

    return data.result.stack[0].value
  }

  /** @deprecated Prefer {@link getRoundEndTimeMs}. Kept for call sites expecting RPC-shaped values. */
  static async getRoundEndTime() {
    return this.getRoundEndTimeMs()
  }

  /** Round active-phase duration in ms (from `getOrbitConfig` + contract defaults). */
  static async getRoundPeriod() {
    const cfg = await this.resolveOrbitConfig()
    return String(cfg.roundDuration)
  }

  static async getTotalRounds() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetTotalRoundsAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'getTotalRounds'],
      id: 1,
    })) as {data: GetTotalRoundsAxiosResponse}

    return data.result.stack[0].value
  }

  static async getUserClaimables(userScriptHash: string) {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<GetUserClaimablesAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [
        scriptHash,
        'getUserClaimables',
        [{type: 'Hash160', value: userScriptHash}],
      ],
      id: 1,
    })) as {data: GetUserClaimablesAxiosResponse}

    const stack0 = data?.result?.stack?.[0]
    const raw = stack0?.value ?? stack0
    return Array.isArray(raw) ? raw : (raw as {value?: unknown[]})?.value ?? []
  }

  static async getClaimablesDecodedForUser(userScriptHash: string) {
    const raw = await this.getUserClaimables(userScriptHash)
    return this.decodeUserClaimablesMap(raw)
  }

  static async isPaused() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<IsPausedAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'isPaused'],
      id: 1,
    })) as {data: IsPausedAxiosResponse}

    return data.result.stack[0].value
  }

  static async isRoundActive() {
    const {rpcUrl, scriptHash} = await this.resolveOrbitContext()

    const {data} = (await axios.post<IsRoundActiveAxiosResponse>(rpcUrl, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [scriptHash, 'isRoundActive'],
      id: 1,
    })) as {data: IsRoundActiveAxiosResponse}

    return data.result.stack[0].value
  }

  static async checkWalletPermissions() {
    try {
      if (!$.walletAdapter.n3Address) {
        $.modal.open('connectWalletModal', {
          networkNamespace: NetworkNamespace.n3,
        })

        throw new Error('You must connect an owner wallet.')
      }

      const owners = await OrbitHelper.getOwners()

      const isOwnerWalletConnected = !!owners.find(
        owner => owner === $.walletAdapter.n3Address
      )

      if (!isOwnerWalletConnected) {
        await $.walletAdapter.disconnectN3Wallet()

        $.modal.open('connectWalletModal', {
          networkNamespace: NetworkNamespace.n3,
        })

        throw new Error(
          'You are being disconnected! Your wallet is not an owner wallet, connect an owner wallet instead.'
        )
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Unexpected error'
      $.toast.abort(message, undefined, false)
    }
  }
}
